import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { EmptyState } from '@/components/EmptyState';
import { ChevronRight, Download } from 'lucide-react';
import { formatDate, formatNumber } from '@/lib/locale';
import { apiGet, qs } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';
import { toast } from '@/lib/toast';

type PolicyPublished = {
  domain?: string;
  p?: string;
  sp?: string;
  pct?: number;
  adkim?: string;
  aspf?: string;
  fo?: string;
  rua?: string;
  ruf?: string;
};

type AggregateReportDetail = {
  id: string;
  reporter_org: string;
  report_id: string;
  email?: string;
  domain_fqdn?: string;
  tenant_slug?: string;
  date_range_begin?: string;
  date_range_end?: string;
  received_at?: string;
  records_count?: number;
  policy_published?: PolicyPublished;
};

type SourceRow = {
  source_ip: string;
  asn?: number | null;
  asn_org?: string | null;
  country?: string | null;
  reverse_dns?: string | null;
  esp_name?: string | null;
  esp_category?: string | null;
  esp_legitimacy?: string | null;
  count?: number | null;
  pass_count?: number;
  fail_count?: number;
  pass_ratio?: number | null;
};

export function ReportDetail({ slug, reportId }: { slug: string; reportId: string }) {
  const reportQ = useQuery({
    queryKey: ['reports', 'aggregate', reportId],
    queryFn: () =>
      apiGet<AggregateReportDetail>(`/reports/aggregate/${reportId}`, {
        tenant: slug,
      }),
  });

  // The records of a report are its per-source-IP rows. /analytics/sources
  // scopes on the reporter's report_id STRING (not our UUID), so wait for the
  // report header to load and pass report.report_id.
  const sourcesQ = useQuery({
    queryKey: ['report', reportId, 'sources', reportQ.data?.report_id],
    enabled: !!reportQ.data?.report_id,
    queryFn: () =>
      apiGet<SourceRow[]>(
        `/analytics/sources${qs({
          tenant: slug,
          report_id: reportQ.data?.report_id,
          reporter_org: reportQ.data?.reporter_org,
          per_page: 50,
        })}`,
        { tenant: slug },
      ),
  });

  const report = reportQ.data;
  const sources = sourcesQ.data ?? [];

  // RAW XML: the API is Bearer-protected, so a plain link 401s — fetch it with
  // the token (apiGet adds Authorization + X-Tenant-Slug) and open the
  // reconstructed XML as an in-memory blob.
  const [xmlBusy, setXmlBusy] = useState(false);
  const openRawXml = async () => {
    // Open the tab SYNCHRONOUSLY inside the click gesture. A window.open() that
    // runs only after the awaited fetch is treated as non-user-initiated and
    // gets swallowed by popup blockers — so we open a blank tab now and merely
    // point it at the blob once the XML arrives. (We drop `noopener` to keep the
    // handle; sever the back-reference instead so the new tab can't reach us.)
    const win = window.open('', '_blank');
    if (win) win.opener = null;
    setXmlBusy(true);
    try {
      const xml = await apiGet<string>(`/reports/aggregate/${reportId}/xml`, { tenant: slug });
      const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
      if (win) {
        win.location.href = url;
      } else {
        // Popup was blocked anyway — last-resort retry (may also be blocked).
        window.open(url, '_blank', 'noopener');
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      win?.close();
      toast({ tone: 'error', message: 'Kon de XML niet ophalen.' });
    } finally {
      setXmlBusy(false);
    }
  };

  return (
    <div className="max-w-[1400px] space-y-5 p-5">
      <QueryState
        isLoading={reportQ.isLoading}
        isError={reportQ.isError}
        onRetry={() => reportQ.refetch()}
      >
        {/* Header */}
        <Card className="p-5">
          <div className="mb-2 text-[11px] text-slate-500">
            <Link
              to="/tenants/$slug/reports"
              params={{ slug }}
              className="inline-flex items-center gap-1 hover:text-slate-900"
            >
              Rapporten
              <ChevronRight size={11} />
            </Link>
            <span>{report?.report_id ?? reportId}</span>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-[20px] font-bold tracking-tight text-slate-900">
                {report?.reporter_org ?? '—'}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-[12px] text-slate-500">
                <span>
                  <strong className="text-slate-700">Report-ID:</strong>{' '}
                  <span className="font-mono">{report?.report_id ?? '—'}</span>
                </span>
                <span className="text-slate-400">·</span>
                <span>
                  <strong className="text-slate-700">Domein:</strong>{' '}
                  <span className="font-mono">{report?.domain_fqdn ?? '—'}</span>
                </span>
                <span className="text-slate-400">·</span>
                <span>
                  <strong className="text-slate-700">Periode:</strong>{' '}
                  {report?.date_range_begin && report?.date_range_end
                    ? `${formatDate(report.date_range_begin)} – ${formatDate(report.date_range_end)}`
                    : '—'}
                </span>
                <span className="text-slate-400">·</span>
                <span>
                  <strong className="text-slate-700">Records:</strong>{' '}
                  <span className="tabular-nums">{formatNumber(report?.records_count ?? 0)}</span>
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={openRawXml}
              disabled={xmlBusy}
              className="inline-flex items-center gap-1.5 rounded border border-slate-200 px-3 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Download size={12} />
              {xmlBusy ? 'Bezig…' : 'RAW XML'}
            </button>
          </div>
        </Card>

        {/* Policy published */}
        <Card>
          <CardHeader title="Gepubliceerd beleid" subtitle="policy_published uit het XML-rapport" />
          <div className="p-4">
            {report?.policy_published ? (
              <pre className="overflow-x-auto rounded bg-slate-900 p-3 font-mono text-[11.5px] leading-relaxed text-slate-50">
                {JSON.stringify(report.policy_published, null, 2)}
              </pre>
            ) : (
              <div className="text-[12px] text-slate-500">
                Geen policy_published in dit rapport.
              </div>
            )}
          </div>
        </Card>

        {/* Records / sources */}
        <Card>
          <CardHeader title="Records" subtitle={`${sources.length} regels uit het XML-rapport`} />
          {sourcesQ.isLoading ? (
            <div className="px-4 py-6 text-[12px] text-slate-500">Laden…</div>
          ) : sources.length === 0 ? (
            <EmptyState
              title="Geen records"
              body="Dit rapport bevat geen records, of ze zijn nog niet geïndexeerd."
              compact
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <Th>IP</Th>
                    <Th>ASN</Th>
                    <Th>ESP</Th>
                    <Th>Geo</Th>
                    <Th align="right">Volume</Th>
                    <Th align="right">Pass-ratio</Th>
                    <Th>Klassificatie</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sources.map((s) => (
                    <tr key={s.source_ip} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5 font-mono text-slate-900">{s.source_ip}</td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-slate-600">
                        {s.asn ?? '—'}
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{s.esp_name ?? '—'}</td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">
                        {s.country ?? '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">
                        {formatNumber(s.count ?? 0)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                        {((s.pass_ratio ?? 0) * 100).toFixed(1).replace('.', ',')} %
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{s.esp_category ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </QueryState>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 text-${align}`}
    >
      {children}
    </th>
  );
}
